import { useMemo } from 'react';

import { NULL_FUNCTION } from '@e-socna/empty';
import type { LatLonCoordinates } from '@e-socna/gis';

import { useIllustrationUrl } from '../../../../components/Illustrations/hooks/useIllustrationUrl.hook';
import { DarkMapIllustration } from '../../../../components/Illustrations/DarkMap/DarkMap';

import { environment } from '../../../../environment';
import { useFromAddressToLatLon } from '../../../../forms/Address/hooks/useFromAddressToLatLon.hook';
import type { AddressForm } from '../../../../forms/Address/types/address-form.type';

import {
  DEFAULT_STATIC_MAP_HEIGHT,
  DEFAULT_STATIC_MAP_WIDTH,
} from '../../../Addresses/constants/card.constant';
import type { AddressCardCmp_Address } from '../../../Addresses/Card';
import type { AddressWithMapCmp_Address } from '../../../AddressWithMap';

interface Params {
  readonly origin?: LatLonCoordinates;
  readonly address?:
    AddressCardCmp_Address | AddressForm | AddressWithMapCmp_Address;
  readonly coordinates?: LatLonCoordinates;
  readonly width?: number;
  readonly height?: number;
}

interface Result {
  readonly navigate: () => void;
  readonly staticMapPreviewUrl: string;
  readonly destinationUrl: string | undefined;
  readonly itineraryUrl: string | undefined;
}

export const useGoogleApi = ({
  origin,
  address,
  coordinates: pCoordinates,
  width = DEFAULT_STATIC_MAP_WIDTH,
  height = DEFAULT_STATIC_MAP_HEIGHT,
}: Readonly<Params>): Result => {
  const mapPlaceholderUrl = useIllustrationUrl(DarkMapIllustration);
  const { data: addressCoordinates } = useFromAddressToLatLon({
    address,
    enabled: !!address && !pCoordinates,
  });

  return useMemo(() => {
    const defaultResult = {
      navigate: NULL_FUNCTION,
      staticMapPreviewUrl: mapPlaceholderUrl,
      destinationUrl: undefined,
      itineraryUrl: undefined,
    };

    const coordinates = pCoordinates ?? addressCoordinates;

    if (!coordinates) {
      return defaultResult;
    }

    const { lat, lon } = coordinates;

    if (lat && lon && Number.isFinite(lat) && Number.isFinite(lon)) {
      const destinationLatAsString = lat.toFixed(6);
      const destinationLonAsString = lon.toFixed(6);

      const destinationUrl = `https://www.google.com/maps/search/?api=1&query=${destinationLatAsString}%2C${destinationLonAsString}`;

      let itineraryUrl = destinationUrl;

      if (origin) {
        const originLatAsString = origin.lat.toFixed(6);
        const originLonAsString = origin.lon.toFixed(6);

        itineraryUrl = `https://www.google.com/maps/dir/?api=1&origin=${originLatAsString}%2C${originLonAsString}&destination=${destinationLatAsString}%2C${destinationLonAsString}`;
      }

      return {
        staticMapPreviewUrl: `https://maps.googleapis.com/maps/api/staticmap?size=${width.toFixed()}x${height.toFixed()}&key=${environment.googleMapsApiKey}&language=fr&markers=${destinationLatAsString},${destinationLonAsString}`,
        destinationUrl,
        itineraryUrl,
        navigate: () =>
          window.open(itineraryUrl, '_blank', 'noopener,noreferrer')?.focus(),
      };
    }

    return defaultResult;
  }, [
    addressCoordinates,
    height,
    mapPlaceholderUrl,
    origin,
    pCoordinates,
    width,
  ]);
};
